import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiClient, SERVICE_PATHS } from '@/lib/api-client';
import type { Document, DocumentAiResults } from '@/types';

/**
 * Fetches documents belonging to a specific owner.
 */
export function useDocuments(ownerId: string) {
  return useQuery<Document[]>({
    queryKey: ['documents', ownerId],
    queryFn: () => apiGet<Document[]>(`${SERVICE_PATHS.documents}/${ownerId}`),
    staleTime: 15_000,
    enabled: ownerId.length > 0,
  });
}

/**
 * Fetches detailed information for a single document by ID.
 */
export function useDocumentDetail(id: string) {
  return useQuery<DocumentAiResults>({
    queryKey: ['document-detail', id],
    queryFn: () => apiGet<DocumentAiResults>(`${SERVICE_PATHS.documents}/${id}`),
    staleTime: 60_000,
    enabled: id.length > 0,
  });
}

interface UploadDocumentInput {
  file: File;
  ownerId: string;
  version: number;
}

/**
 * Uploads a document as multipart form data.
 * Invalidates the documents query for the owner on success.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, Error, UploadDocumentInput>({
    mutationFn: async ({ file, ownerId, version }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ownerId', ownerId);
      formData.append('version', String(version));

      return apiClient<Document>(SERVICE_PATHS.upload, {
        method: 'POST',
        body: formData,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents', variables.ownerId] });
    },
  });
}
